import { withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import Input from 'components/final-form/Input';
import { CodeInput } from 'components/final-form/Inputs';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addPatientDocumentType, editPatientDocumentType } from 'services/patientDocumentTypeServices';
import { UpsertPatientDocumentTypeDto } from 'ts-types/api.types';


interface Props extends MessageSourceProps {
  onSave: () => void,
  onCancel: () => void,
  patientDocumentType?: UpsertPatientDocumentTypeDto,
  patientId?: number,
  code?: string
}

interface UpsertPatientDocumentTypeDtoValues extends UpsertPatientDocumentTypeDto {
  code: string
}

const cancelTokenSource = axios.CancelToken.source();

const UpsertPatientDocumentType = (props: Props) => {

  const {onSave, onCancel, patientDocumentType, code, getMessage} = props;

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const initialValues: Partial<UpsertPatientDocumentTypeDtoValues> = {
    code: code,
    ...patientDocumentType
  }

  const handleSubmit = async (values: UpsertPatientDocumentTypeDtoValues) => {

    let saveValues: UpsertPatientDocumentTypeDtoValues = {
      ...values,
      active: true
    };

    const handleError = (error: any) => {
      setErrorMessage(error.response.data.violations[0].details);
    };

    if (code) {
      await editPatientDocumentType(code, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => handleError(e))
    } else {
      await addPatientDocumentType(saveValues.code, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => handleError(e))
    }
  }

  const renderFinalForm = (): React.ReactNode => {
    return <FinalForm
        onSubmit={(values: any) => handleSubmit(values)}
        initialValues={initialValues}
        subscription={{values: true, pristine: true, submitting: true}}
        render={renderForm}
    />;
  }

  const renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }

              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    {/*<Header>{headerMessage}</Header>*/}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("patientRecord.patientDocumentType.code")}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="code"
                        component={CodeInput}
                        fluid
                        disabled={!!code}
                        required
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("patientRecord.patientDocumentType.label.descriptionEn")}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="description.textEn"
                        component={Input}
                        fluid
                        required
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("patientRecord.patientDocumentType.label.descriptionDe")}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="description.textDe"
                        component={Input}
                        fluid
                        required
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("patientRecord.patientDocumentType.label.descriptionFr")}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="description.textFr"
                        component={Input}
                        fluid
                        required
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("patientRecord.patientDocumentType.label.descriptionIt")}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="description.textIt"
                        component={Input}
                        fluid
                        required
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
              <FormButtonsRow>
                <Button compact type="submit" primary disabled={submitting}>
                  {getMessage("button.save")}
                </Button>
                <Button compact type="button" disabled={submitting}
                        onClick={onCancel}>{getMessage("button.cancel")}
                </Button>
              </FormButtonsRow>
            </FormContent>
          </FormContainer>
        </form>
    )
  }

  return renderFinalForm();
}

export default withAuthContext(withMessages(UpsertPatientDocumentType));
